/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ProductsDataActions, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';
import { IProductAttributeGroup } from '../../../actions/DataServiceEntities.g';
import { getGroupedAttributesAsync } from '../../../actions/DataActionExtension.g';

/**
 * Chains getting the SelectedVariant with GetActivePrice.
 */
export class AttributeGroupsForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId: number;

    public selectedProduct: SimpleProduct | undefined;

    constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, catalogId?: number) {
        this.productId = productId;
        this.channelId = channelId;
        this.selectedProduct = selectedProduct;
        this.catalogId = catalogId || 0;
    }

    public getCacheKey = () => `AttributeGroupsForSelectedVariant-${this.productId}`;

    public getCacheObjectType = () => 'ProductAttributeGroup';

    public dataCacheType = (): CacheType => 'instance';
}

export const createAttributeGroupsForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): AttributeGroupsForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new AttributeGroupsForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create AttributeGroupsForSelectedVariantInput, no productId found on module config or query');
};

/**
 * Calls the Retail API a get a list of dimension values for a given product.
 * @param input
 * @param ctx
 */
export async function getAttributeGroupsForSelectedVariantAction(
    input: AttributeGroupsForSelectedVariantInput,
    ctx: IActionContext
): Promise<IProductAttributeGroup[]> {
    return Promise.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                return ProductsDataActions.getByIdAsync({ callerContext: ctx }, input.productId, input.channelId);
            }

            return activeProduct;
        })
        .then<IProductAttributeGroup[] | null>((productResult: SimpleProduct | null) => {
            const activeProduct: SimpleProduct | undefined = <SimpleProduct | undefined>productResult;

            if (activeProduct) {
                let recId = activeProduct.RecordId;

                const newSkuInfo = activeProduct.ExtensionProperties?.find(ep => ep.Key === 'NewSku')?.Value?.StringValue;
                if (newSkuInfo) {
                    const newProductRecId = newSkuInfo.split(':')[0];
                    if (+newProductRecId) {
                        recId = +newProductRecId;
                    }
                }

                return getGroupedAttributesAsync({ callerContext: ctx, queryResultSettings: {} }, input.channelId, input.catalogId, recId);
            }

            return null;
        })
        .then((attributeValues: IProductAttributeGroup[] | null) => {
            const resultArray = <IProductAttributeGroup[]>attributeValues;
            if (resultArray) {
                return resultArray;
            }

            return [];
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            throw new Error('[getPriceForSelectedVariantAction]Error executing action');
        });
}

export default createObservableDataAction({
    id: 'hei-product-info/get-attribute-groups-for-selected-variant',
    action: <IAction<IProductAttributeGroup[]>>getAttributeGroupsForSelectedVariantAction,
    input: createAttributeGroupsForSelectedVariantInput
});
